import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth, API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { 
  Gift, Zap, Loader2, Copy, Check, Music, Share2, Video
} from 'lucide-react';
import MainNav from '../components/MainNav';
import SongsSidebar from '../components/SongsSidebar';
import { CoachEye } from '../components/Coach';

export default function CreateGift() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [isCreating, setIsCreating] = useState(false);
  const [songs, setSongs] = useState([]);
  const [videos, setVideos] = useState([]);
  const [covers, setCovers] = useState([]);
  const [mediaType, setMediaType] = useState('song'); // 'song' or 'video'
  const [selectedSong, setSelectedSong] = useState(location.state?.songId || '');
  const [selectedVideo, setSelectedVideo] = useState(location.state?.videoId || '');
  const [selectedCover, setSelectedCover] = useState(location.state?.coverId || '');
  const [recipientName, setRecipientName] = useState('');
  const [message, setMessage] = useState('');
  const [createdGift, setCreatedGift] = useState(null);
  const [copied, setCopied] = useState(false);
  
  // Dynamic sidebar height to match left content
  const leftContentRef = useRef(null);
  const [sidebarHeight, setSidebarHeight] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [songsRes, videosRes, coversRes] = await Promise.all([
          fetch(`${API}/songs`, { credentials: 'include' }),
          fetch(`${API}/videos`, { credentials: 'include' }),
          fetch(`${API}/covers`, { credentials: 'include' })
        ]);
        
        if (songsRes.ok) {
          const data = await songsRes.json();
          console.log('CreateGift: Raw songs data:', data.length);
          const filtered = data.filter(s => s.status === 'completed' && s.audio_url);
          console.log('CreateGift: Filtered songs:', filtered.length);
          setSongs(filtered);
        }
        if (videosRes.ok) {
          const data = await videosRes.json();
          console.log('CreateGift: Raw videos data:', data.length);
          const filtered = data.filter(v => v.status === 'completed' && v.video_url);
          console.log('CreateGift: Filtered videos:', filtered.length);
          setVideos(filtered);
        }
        if (coversRes.ok) {
          const data = await coversRes.json();
          setCovers(data);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  const handleCreate = async () => {
    if (mediaType === 'song' && !selectedSong) {
      toast.error('Please select a song');
      return;
    }
    if (mediaType === 'video' && !selectedVideo) {
      toast.error('Please select a video');
      return;
    }
    if (!message.trim()) {
      toast.error('Please add a message');
      return;
    }

    setIsCreating(true);
    try {
      const res = await fetch(`${API}/gifts/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          song_id: mediaType === 'song' ? selectedSong : null,
          video_id: mediaType === 'video' ? selectedVideo : null,
          cover_id: selectedCover || null,
          recipient_name: recipientName || null,
          message
        })
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Failed to create gift card');
      }

      const data = await res.json();
      setCreatedGift(data);
      toast.success('Gift card created!');
    } catch (error) {
      console.error('Gift creation error:', error);
      toast.error(error.message || 'Failed to create gift card');
    } finally {
      setIsCreating(false);
    }
  };

  const handleCopyLink = () => {
    const shareUrl = `${window.location.origin}/gift/${createdGift.gift_id}`;
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Link copied!');
  };

  const selectedSongData = songs.find(s => s.song_id === selectedSong);
  const selectedVideoData = videos.find(v => v.video_id === selectedVideo);
  const selectedCoverData = covers.find(c => c.cover_id === selectedCover);

  // Measure left content height for dynamic sidebar sizing
  useEffect(() => {
    const measureHeight = () => {
      if (leftContentRef.current) {
        setSidebarHeight(leftContentRef.current.offsetHeight);
      }
    };
    
    measureHeight();
    // Re-measure on window resize
    window.addEventListener('resize', measureHeight);
    return () => window.removeEventListener('resize', measureHeight);
  }, [createdGift, selectedSong, selectedVideo, mediaType]); // Re-measure when content changes

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <MainNav />

      {/* Main Content with Sidebar */}
      <main className="pt-24 pb-12 px-4 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8 flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">🎁 Send a music gift</h1>
              <p className="text-zinc-400">Turn your song into something unforgettable</p>
            </div>
            <Button 
              onClick={() => navigate('/my-gifts')} 
              variant="outline" 
              className="border-white/20 text-zinc-300 hover:bg-white/10"
            >
              <Gift className="h-4 w-4 mr-2" />
              My Gift Cards
            </Button>
          </div>

          {/* Flex layout - Form/Preview grid + Sidebar */}
          <div className="flex gap-8 items-start">
            <div ref={leftContentRef} className="flex-1 min-w-0">
              {!createdGift ? (
                <div className="grid lg:grid-cols-2 gap-8">
                  {/* Form */}
                  <div className="glass-card p-6" data-testid="gift-form">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-lg bg-pink-500/20 flex items-center justify-center">
                      <Gift className="h-5 w-5 text-pink-500" />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold text-white">✨ Create your gift</h2>
                      <p className="text-sm text-zinc-400">Free to create</p>
                    </div>
                  </div>

                  <div className="space-y-6">
                    {/* Media Type Toggle */}
                    <div>
                      <Label className="text-white mb-2 block">🎬 What type of gift?</Label>
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            setMediaType('song');
                            setSelectedVideo('');
                          }}
                          className={`p-3 rounded-lg border transition-all flex items-center justify-center gap-2 ${
                            mediaType === 'song'
                              ? 'border-primary bg-primary/20 text-white'
                              : 'border-white/10 bg-white/5 text-zinc-400 hover:border-white/30'
                          }`}
                        >
                          <Music className="h-4 w-4" />
                          Song (MP3)
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setMediaType('video');
                            setSelectedSong('');
                          }}
                          className={`p-3 rounded-lg border transition-all flex items-center justify-center gap-2 ${
                            mediaType === 'video'
                              ? 'border-primary bg-primary/20 text-white'
                              : 'border-white/10 bg-white/5 text-zinc-400 hover:border-white/30'
                          }`}
                        >
                          <Video className="h-4 w-4" />
                          Video (MP4)
                        </button>
                      </div>
                    </div>

                    {/* Song Selection */}
                    {mediaType === 'song' && (
                      <div>
                        <Label className="text-white mb-2 block">🎵 Choose a song</Label>
                        <select
                          value={selectedSong}
                          onChange={(e) => setSelectedSong(e.target.value)}
                          className="custom-select w-full"
                          data-testid="song-select"
                        >
                          <option value="">Choose a song</option>
                          {songs.map((song) => (
                            <option key={song.song_id} value={song.song_id}>{song.title}</option>
                          ))}
                        </select>
                      </div>
                    )}

                    {/* Video Selection */}
                    {mediaType === 'video' && (
                      <div>
                        <Label className="text-white mb-2 block">🎬 Choose a video</Label>
                        <select
                          value={selectedVideo}
                          onChange={(e) => setSelectedVideo(e.target.value)}
                          className="custom-select w-full"
                          data-testid="video-select"
                        >
                          <option value="">Choose a video</option>
                          {videos.map((video) => (
                            <option key={video.video_id} value={video.video_id}>{video.title}</option>
                          ))}
                        </select>
                        {videos.length === 0 && (
                          <p className="text-xs text-zinc-500 mt-2">No completed videos yet. Create one in the Videos section!</p>
                        )}
                      </div>
                    )}

                    {/* Cover Selection - Removed, now at bottom */}

                    {/* Recipient Name */}
                    <div>
                      <Label className="text-white mb-2 flex items-center gap-2">
                        <span>👤 Who is this for? (optional)</span>
                        <CoachEye targetId="gift.create" label="Create your gift" />
                      </Label>
                      <Input
                        value={recipientName}
                        onChange={(e) => setRecipientName(e.target.value)}
                        placeholder="e.g., Sarah"
                        className="input-dark"
                        data-testid="recipient-input"
                      />
                    </div>

                    {/* Message */}
                    <div>
                      <Label className="text-white mb-2 block">💌 Add your message</Label>
                      <textarea
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder="e.g. Happy Birthday! This song made me think of you…"
                        className="textarea-dark w-full h-24"
                        data-testid="message-input"
                      />
                    </div>

                    {/* Create Button */}
                    <Button
                      onClick={handleCreate}
                      disabled={isCreating || (mediaType === 'song' ? !selectedSong : !selectedVideo) || !message.trim()}
                      className="btn-primary w-full"
                      data-testid="create-gift-btn"
                    >
                      {isCreating ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Creating...
                        </>
                      ) : (
                        <>
                          🎁 Create My Gift Card (Free)
                        </>
                      )}
                    </Button>
                  </div>
                </div>

                {/* Preview */}
                <div className="glass-card p-4">
                  <h3 className="text-base font-bold text-white mb-3">👀 Your gift</h3>
              
                  {(mediaType === 'song' && !selectedSong) || (mediaType === 'video' && !selectedVideo) ? (
                    <div className="aspect-square rounded-lg bg-black/30 flex items-center justify-center">
                      <div className="text-center text-zinc-500">
                        <Gift className="h-12 w-12 mx-auto mb-3 opacity-50" />
                        <p>Your gift will appear here</p>
                      </div>
                    </div>
                  ) : (
                  <div className="bg-gradient-to-br from-pink-500/10 to-primary/10 rounded-lg p-4 border border-white/10">
                    {/* Cover Image or Video Thumbnail */}
                    <div className="aspect-square rounded-lg overflow-hidden bg-black/30 mb-3 max-w-[120px] mx-auto">
                      {mediaType === 'video' && selectedVideoData ? (
                        // Video thumbnail
                        selectedVideoData.thumbnail_url ? (
                          <img 
                            src={selectedVideoData.thumbnail_url}
                            alt="Video thumbnail"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-purple-900/50 to-indigo-900/50">
                            <Video className="h-6 w-6 text-white/40 mb-1" />
                            <span className="text-[10px] text-white/40">Video</span>
                          </div>
                        )
                      ) : selectedCoverData?.image_url ? (
                        <img 
                          src={selectedCoverData.image_url}
                          alt="Cover"
                          className="w-full h-full object-cover"
                        />
                      ) : selectedCoverData?.image_base64 ? (
                        <img 
                          src={`data:image/png;base64,${selectedCoverData.image_base64}`}
                          alt="Cover"
                          className="w-full h-full object-cover"
                        />
                      ) : selectedSongData?.image_url && selectedCover !== '' ? (
                        <img 
                          src={selectedSongData.image_url}
                          alt="Song"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-purple-900/50 to-indigo-900/50">
                          <Music className="h-6 w-6 text-white/40 mb-1" />
                          <span className="text-[10px] text-white/40">Text Only</span>
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div className="text-center">
                      {recipientName && (
                        <p className="text-zinc-400 text-xs mb-1">For {recipientName}</p>
                      )}
                      <h4 className="text-base font-bold text-white mb-1">
                        {mediaType === 'video' 
                          ? (selectedVideoData?.title || 'Choose a video to start your gift')
                          : (selectedSongData?.title || 'Choose a song to start your gift')}
                      </h4>
                      <p className="text-zinc-300 text-xs mb-2 italic line-clamp-2">
                        "{message || 'Your message here...'}"
                      </p>
                      <div className="text-[10px] text-zinc-500">
                        Created with WrappedInMusic
                      </div>
                    </div>
                  </div>
                  )}
                </div>
              </div>
            ) : (
              /* Success State */
              <div className="glass-card p-8 text-center max-w-md mx-auto">
                <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
                  <Gift className="h-8 w-8 text-green-500" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">Gift Card Created!</h2>
                <p className="text-zinc-400 mb-6">Share this link with your recipient:</p>
            
                <div className="flex gap-2 mb-6">
                  <Input
                    value={`${window.location.origin}/gift/${createdGift.gift_id}`}
                    readOnly
                    className="input-dark flex-1"
                  />
                  <Button onClick={handleCopyLink} className="btn-secondary">
                    {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>

                <div className="flex gap-4">
                  <Button onClick={() => navigate(`/gift/${createdGift.gift_id}`)} className="btn-primary flex-1">
                    View Gift
                  </Button>
                  <Button onClick={() => setCreatedGift(null)} className="btn-secondary flex-1">
                    Create Another
                  </Button>
                </div>
              </div>
            )}
            </div>

            {/* Songs Sidebar - Dynamic height to match left content */}
            <SongsSidebar maxHeight={sidebarHeight} />
          </div>
        </div>

        {/* Album Covers - Horizontal Scroll (full width, below sidebar) */}
        {!createdGift && covers.length > 0 && (
          <div className="max-w-7xl mx-auto mt-8">
            <h2 className="text-xl font-bold text-white mb-4">🖼️ Choose a cover (optional)</h2>
            <p className="text-zinc-400 text-sm mb-6">Pick a look for your gift</p>
            <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-thin scrollbar-thumb-white/20 scrollbar-track-transparent">
              {/* None option */}
              <button
                onClick={() => setSelectedCover('')}
                className={`flex-shrink-0 w-32 h-32 sm:w-40 sm:h-40 rounded-lg overflow-hidden border-2 transition-all ${
                  !selectedCover ? 'border-primary ring-2 ring-primary/50' : 'border-white/10 hover:border-white/30'
                }`}
              >
                <div className="w-full h-full bg-black/30 flex flex-col items-center justify-center text-zinc-400">
                  <span className="text-sm font-medium">Text Only</span>
                  <span className="text-xs opacity-70">Simple and clean</span>
                </div>
              </button>
              {covers.map((cover) => (
                <button
                  key={cover.cover_id}
                  onClick={() => setSelectedCover(cover.cover_id)}
                  className={`flex-shrink-0 w-32 h-32 sm:w-40 sm:h-40 rounded-lg overflow-hidden border-2 transition-all ${
                    selectedCover === cover.cover_id ? 'border-primary ring-2 ring-primary/50' : 'border-white/10 hover:border-white/30'
                  }`}
                >
                  {cover.image_url ? (
                    <img 
                      src={cover.image_url}
                      alt="Cover"
                      className="w-full h-full object-cover"
                    />
                  ) : cover.image_base64 ? (
                    <img 
                      src={`data:image/png;base64,${cover.image_base64}`}
                      alt="Cover"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-black/30 flex items-center justify-center">
                      <Music className="h-8 w-8 text-white/20" />
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
